/**
 * Google Ads conversion tracking with Enhanced Conversions support.
 *
 * Config is loaded at RUNTIME from GET /api/site/google-ads (set in
 * Admin → Marketing → Google Ads), so the operator can plug in / rotate
 * their Conversion ID + per-goal labels with NO redeploy. Env vars are
 * used only as a fallback if the API config is empty:
 *   REACT_APP_GOOGLE_ADS_ID, REACT_APP_GADS_LABEL_PURCHASE, ...
 *
 * Why a separate file from analytics.js?
 *   - GA4 wants generic event names ("purchase", "add_to_cart").
 *   - Google Ads needs a SECOND gtag('event','conversion',{send_to:...})
 *     with a SPECIFIC conversion label per goal.
 *
 * Until a Conversion ID is configured, every function below is a silent no-op.
 */
import { getGoogleAdsPublic } from "./api";

const ENV_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "AW-18264990337";
const ENV_LABELS = {
  purchase: process.env.REACT_APP_GADS_LABEL_PURCHASE || "Ya8WCOyq_eccEIHFtoVE",
  quote: process.env.REACT_APP_GADS_LABEL_QUOTE || "f5bACLHGiegcEIHFtoVE",
  add_to_cart: process.env.REACT_APP_GADS_LABEL_ADD_TO_CART || "JG-5CKfFjOgcEIHFtoVE",
  begin_checkout: process.env.REACT_APP_GADS_LABEL_BEGIN_CHECKOUT || "06qWCN-1lOgcEIHFtoVE",
};

let _configPromise = null;
let _bootstrapped = false;

/** Fetch (and cache) the runtime config, merged over env fallbacks. */
function loadConfig() {
  if (_configPromise) return _configPromise;
  _configPromise = getGoogleAdsPublic()
    .then((cfg) => {
      const id = (cfg?.conversion_id || "").trim() || ENV_ID;
      const enabled = cfg?.enabled !== false && !!id;
      const labels = {
        purchase: cfg?.labels?.purchase || ENV_LABELS.purchase,
        quote: cfg?.labels?.quote || ENV_LABELS.quote,
        add_to_cart: cfg?.labels?.add_to_cart || ENV_LABELS.add_to_cart,
        begin_checkout: cfg?.labels?.begin_checkout || ENV_LABELS.begin_checkout,
      };
      return { id, enabled, labels };
    })
    .catch(() => ({ id: ENV_ID, enabled: !!ENV_ID, labels: ENV_LABELS }));
  return _configPromise;
}

/** Issue the additional gtag config for the AW property (remarketing + EC). */
function bootstrap(id) {
  if (_bootstrapped || !id || typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return; // GA4 script not loaded yet
  window.gtag("config", id, { allow_enhanced_conversions: true });
  _bootstrapped = true;
}

/** Called by GoogleAdsLoader on app boot to warm the config + config gtag. */
export async function initGoogleAds() {
  const cfg = await loadConfig();
  if (cfg.enabled) bootstrap(cfg.id);
  return cfg;
}

/** Web Crypto SHA-256 → lowercase hex (Enhanced Conversions). */
async function sha256Hex(str) {
  if (!str || typeof window === "undefined" || !window.crypto?.subtle) return "";
  try {
    const data = new TextEncoder().encode(str.trim().toLowerCase());
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return "";
  }
}

/** Normalise a Canadian phone number to E.164 before hashing. */
function normalisePhone(phone) {
  if (!phone) return "";
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("1") && digits.length === 11) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

/**
 * Fire a Google Ads conversion.
 *   - labelKey: which configured label to use (purchase|quote|add_to_cart|begin_checkout)
 *   - label:    OR pass a raw label directly (overrides labelKey)
 *   - value / currency / transactionId / userData{email,phone}
 */
export async function trackAdsConversion({
  labelKey,
  label,
  value = 0,
  currency = "CAD",
  transactionId,
  userData = {},
}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const cfg = await loadConfig();
  if (!cfg.enabled || !cfg.id) return;
  const resolvedLabel = label || (labelKey ? cfg.labels[labelKey] : "");
  if (!resolvedLabel) return; // no label configured for this goal yet
  bootstrap(cfg.id);

  // Enhanced Conversions — set hashed user_data BEFORE the conversion event.
  const { email, phone } = userData;
  if (email || phone) {
    const [sha_email, sha_phone] = await Promise.all([
      email ? sha256Hex(email) : Promise.resolve(""),
      phone ? sha256Hex(normalisePhone(phone)) : Promise.resolve(""),
    ]);
    const ud = {};
    if (sha_email) ud.sha256_email_address = sha_email;
    if (sha_phone) ud.sha256_phone_number = sha_phone;
    if (Object.keys(ud).length) window.gtag("set", "user_data", ud);
  }

  const params = {
    send_to: `${cfg.id}/${resolvedLabel}`,
    value: Number(value).toFixed(2),
    currency,
  };
  if (transactionId) params.transaction_id = transactionId;
  window.gtag("event", "conversion", params);
}

// Convenience shortcuts — one per goal so call sites stay terse.
export const trackAdsPurchase = (args) =>
  trackAdsConversion({ ...args, labelKey: "purchase" });
export const trackAdsQuote = (args) =>
  trackAdsConversion({ ...args, labelKey: "quote" });
export const trackAdsAddToCart = (args) =>
  trackAdsConversion({ ...args, labelKey: "add_to_cart" });
export const trackAdsBeginCheckout = (args) =>
  trackAdsConversion({ ...args, labelKey: "begin_checkout" });
